import { type EligibleProduct, useProductOfferStore } from "@expert/sales-tools";
import { useEffect, useEffectEvent } from "react";

import { getRootDispatcher } from "@/analytics";
import { isSimulatedVoiceTask, useLogger, useSessionId, useTypedMostRecentTask } from "@/sdk";
import { usePartner } from "@/sdk/agent/hooks/usePartner";

import { getProductOffer } from "../api/getProductOffer";
import { updatePrimaryProductSaleProgress } from "../utils/updatePrimaryProductSaleProgress";

export const useQuerySelectedProductOffer = () => {
    const logger = useLogger({ module: "useQuerySelectedProductOffer", supportTeam: "sales" });
    const partner = usePartner();
    const sessionId = useSessionId();
    const { task } = useTypedMostRecentTask();
    const selectedProduct = useProductOfferStore((state) => state.getSelectedProduct(sessionId));
    const isExpertUniversitySimulatedVoiceTask = isSimulatedVoiceTask(task);

    const onUpdate = useEffectEvent(async (selectedProduct: EligibleProduct) => {
        try {
            // In training mode, we don't want to call the product-offer API since its response does not matter for training
            const isTrainingProduct =
                isExpertUniversitySimulatedVoiceTask && selectedProduct.salesProvider === "expert-university";

            if (isTrainingProduct) {
                return;
            }

            const { enrollmentStatus } = await getProductOffer(
                logger,
                selectedProduct.id,
                partner,
                selectedProduct.salesProvider,
            );

            if (enrollmentStatus == null) {
                return;
            }

            updatePrimaryProductSaleProgress({
                offerId: selectedProduct.id,
                status: enrollmentStatus,
                salesProvider: selectedProduct.salesProvider,
            });
        } catch (error) {
            void getRootDispatcher().dispatchBusinessEvent("GetProductOfferError", {
                productOfferId: selectedProduct.id,
                partner,
                error,
            });
        }
    });

    useEffect(() => {
        if (selectedProduct == null) {
            return; // no active product
        }

        void onUpdate(selectedProduct);
    }, [partner, selectedProduct]);
};
